import { useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export default function RichTextEditor({ value, onChange, placeholder, className = '' }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isUpdatingRef = useRef(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (editorRef.current && !isUpdatingRef.current) {
      // Only update if content is different
      if (editorRef.current.innerHTML !== value) {
        const selection = window.getSelection();
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        const startOffset = range?.startOffset || 0;
        const endOffset = range?.endOffset || 0;
        
        editorRef.current.innerHTML = value;
        
        // Restore cursor position
        if (range && editorRef.current.firstChild) {
          try {
            const newRange = document.createRange();
            // Find a suitable text node
            let textNode = editorRef.current.firstChild;
            while (textNode && textNode.nodeType !== Node.TEXT_NODE) {
              textNode = textNode.firstChild || textNode.nextSibling;
            }
            
            if (textNode && textNode.nodeType === Node.TEXT_NODE) {
              const textLength = textNode.textContent?.length || 0;
              newRange.setStart(textNode, Math.min(startOffset, textLength));
              newRange.setEnd(textNode, Math.min(endOffset, textLength));
              selection?.removeAllRanges();
              selection?.addRange(newRange);
            }
          } catch (e) {
            // Ignore errors with cursor positioning
          }
        }
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      isUpdatingRef.current = true;
      onChange(editorRef.current.innerHTML);
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  };

  const applyFormat = (command: string, value?: string) => {
    try {
      document.execCommand(command, false, value);
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
      editorRef.current?.focus();
    } catch (e) {
      console.error('Failed to apply format:', command, e);
    }
  };

  const applyColor = (color: string) => {
    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.getRangeAt(0).collapsed) {
        // No selection or collapsed selection, set color for next input
        document.execCommand('foreColor', false, color);
      } else {
        // Apply color to selected text
        document.execCommand('foreColor', false, color);
      }
      
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
      editorRef.current?.focus();
    } catch (e) {
      console.error('Failed to apply color:', color, e);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className={`border border-gray-300 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className={`flex items-center gap-2 p-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'}`}>
        <button
          type="button"
          onClick={() => applyFormat('bold')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Bold"
        >
          <i className={`ri-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}></i>
        </button>
        <button
          type="button"
          onClick={() => applyFormat('italic')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Italic"
        >
          <i className={`ri-italic ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}></i>
        </button>
        <button
          type="button"
          onClick={() => applyFormat('underline')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Underline"
        >
          <i className={`ri-underline ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}></i>
        </button>
        
        <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
        
        {/* Color Buttons */}
        <button
          type="button"
          onClick={() => applyColor('#000000')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Black"
        >
          <div className="w-5 h-5 rounded bg-black border border-gray-300"></div>
        </button>
        <button
          type="button"
          onClick={() => applyColor('#ef4444')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Red"
        >
          <div className="w-5 h-5 rounded bg-red-500 border border-gray-300"></div>
        </button>
        <button
          type="button"
          onClick={() => applyColor('#f97316')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Orange"
        >
          <div className="w-5 h-5 rounded bg-orange-500 border border-gray-300"></div>
        </button>
        <button
          type="button"
          onClick={() => applyColor('#eab308')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Yellow"
        >
          <div className="w-5 h-5 rounded bg-yellow-500 border border-gray-300"></div>
        </button>
        <button
          type="button"
          onClick={() => applyColor('#22c55e')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Green"
        >
          <div className="w-5 h-5 rounded bg-green-500 border border-gray-300"></div>
        </button>
        <button
          type="button"
          onClick={() => applyColor('#3b82f6')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Blue"
        >
          <div className="w-5 h-5 rounded bg-blue-500 border border-gray-300"></div>
        </button>
        <button
          type="button"
          onClick={() => applyColor('#a855f7')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Purple"
        >
          <div className="w-5 h-5 rounded bg-purple-500 border border-gray-300"></div>
        </button>
        <button
          type="button"
          onClick={() => applyColor('#ec4899')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Pink"
        >
          <div className="w-5 h-5 rounded bg-pink-500 border border-gray-300"></div>
        </button>
        <button
          type="button"
          onClick={() => applyColor('#e5e7eb')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
          title="Light Gray (Default for Dark Mode)"
        >
          <div className="w-5 h-5 rounded bg-gray-200 border border-gray-300"></div>
        </button>
      </div>
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className={`min-h-[100px] p-3 focus:outline-none ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}
        style={{ backgroundColor: isDarkMode ? '#1f2937' : '#ffffff' }}
        data-placeholder={placeholder}
      />
      
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] {
          caret-color: currentColor;
        }
      `}</style>
    </div>
  );
}
